import { Checkbox, Select, TimePicker } from "antd";

import TooltipButton from "../../../Template/Buttons/TooltipButton";
import { TranscriptionBlockType } from "../../../../utils/type";
import { secondsToCustomFormat } from "../../../../utils/convertSecondToHour";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { speakerStyles } from "../../../../utils/speakerStyle";
import { useAppDispatch } from "../../../../redux/useAppDispatch";
import { clearMergeBlock, createNewSegment, deleteBlock, mergeBlock, handleMergeBlock, handleMergeBlockSelect, setCurrentTimeValue, setPlaying, splitSegment, updateBlock, openQcDetailAudioBlockDeleteModal, setSelectedTranscriptionBlock, updateTime, resetHighlightText } from "../../../../redux/JobModule/QCDetailPage/QCDetailPageAction";
import { calculateLengthPerWord } from "../../../../utils/calculateLengthPerWord";
import { wordCount } from "../../../../utils/wordCount";
import { LoadingOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { set } from "lodash";

interface Props {
  transcript: TranscriptionBlockType;
  disabled: boolean;
  currentText: string;
  index: number;
  lastElement: boolean;
}

function TextAreaActionPanel(props: Props) {
  const dispatch = useAppDispatch();

  // Get speaker options and index
  const speakers = useSelector((state: RootState) => state.qCDetailPageState.data.transcriptions[0].speakers) as string[];
  const speakerIndex = speakers.indexOf(props.transcript.speaker);
  const speakerOption = speakers.map((speaker: string) => ({
    label: speaker,
    value: speaker,
  }));
  const idTranscription = useSelector((state: RootState) => state.qCDetailPageState.data.transcriptions[0].idTranscription);
  const currentTime = useSelector((state: RootState) => state.qCDetailPageState.currentTime);
  const selectBlockIds = useSelector((state: RootState) => state.qCDetailPageState.mergeBlockId);
  const isPlaying = useSelector((state: RootState) => state.qCDetailPageState.isPlaying);
  const mergeLoading = useSelector((state: RootState) => state.qCDetailPageState.mergeBlockLoading);
  const highlightTextState = useSelector((state: RootState) => state.qCDetailPageState.highlightedText);
  const createNewSegmentLoading = useSelector((state: RootState) => state.qCDetailPageState.createNewSegmentLoading);
  const splitSegmentLoading = useSelector((state: RootState) => state.qCDetailPageState.splitSegmentLoading);
  const deleteSegmentLoading = useSelector((state: RootState) => state.qCDetailPageState.deleteLoading);
  const updateBlockLoading = useSelector((state: RootState) => state.qCDetailPageState.updateBlockLoading);
  const endTime = useSelector((state: RootState) => state.qCDetailPageState.endTime);
  const updateTimeLoading = useSelector((state: RootState) => state.qCDetailPageState.updateTimeLoading);
  const [startTime, setStartTime] = useState(props.transcript.startTimeSec);
  const [endTimeSec, setEndTime] = useState(props.transcript.endTimeSec);
  useEffect(() => {
    setStartTime(props.transcript.startTimeSec);
    setEndTime(props.transcript.endTimeSec);
  }, [props.transcript]);

  /**
   * Update the speaker for the current transcription block.
   * @param value - The new speaker value.
   */
  const updateSpeaker = async (value: string) => {
    const newBlock = {
      TranscriptionId: idTranscription,
      TranscriptionBlocks: [
        {
          TranscriptionBlockId: props.transcript.idTranscriptionBlock,
          StartTimeSec: props.transcript.startTimeSec,
          EndTimeSec: props.transcript.endTimeSec,
          Speaker: value,
          Text: props.transcript.transcript,
        },
      ],
    };
    await dispatch(
      updateBlock({
        id: idTranscription,
        data: newBlock,
        blockIndex: props.index,
      })
    );
  };

  /**
   * Get the color associated with the speaker.
   * @returns The color for the speaker.
   */
  const speakerColor = () => {
    if (speakerIndex < 0) {
      return speakerStyles[speakerStyles.length - 1].color;
    } else {
      return speakerStyles[speakerIndex].color;
    }
  };

  /**
   * Handle merging of selected transcription blocks.
   */
  const handleMergeBlockCall = async () => {
    if (selectBlockIds.length === 1) {
      dispatch(clearMergeBlock());
      return;
    }
    const payload = {
      TranscriptionId: idTranscription,
      BlockIds: selectBlockIds,
    };
    await dispatch(mergeBlock(payload));
    dispatch(clearMergeBlock());
  };

  /**
   * Handle deletion of the current transcription block.
   */
  const handleDeleteBlock = async () => {
    await dispatch(
      deleteBlock({
        idTranscription: idTranscription,
        idBlock: props.transcript.idTranscriptionBlock,
      })
    );
  };

  /**
   * Handle adding a new transcription block.
   */
  const handleAddBlock = async () => {
    const payload = {
      TranscriptionId: idTranscription,
      NewBlocks: [
        {
          StartTimeSec: props.transcript.startTimeSec + 1,
          EndTimeSec: props.transcript.startTimeSec + 1,
          Speaker: props.transcript.speaker,
          Text: "",
        },
      ],
      TranscriptionBlocks: [],
    };
    await dispatch(createNewSegment({ id: idTranscription, data: payload }));
  };

  /**
   * Handle splitting the current transcription block based on highlighted text.
   */

  const isLargerThanEndTime = (time: number, endTime: number) => {
    if (time > endTime) {
      return endTime - 1;
    } else {
      return time;
    }
  };
  const handleSplitBlockCall = async () => {
    const { transcript } = props;
    const { type, highlightText, remainingText } = highlightTextState;
    if (highlightText === "") {
      return;
    }

    const lengthPerWord = calculateLengthPerWord(props.transcript.endTimeSec - props.transcript.startTimeSec, wordCount(props.currentText));
    const highlightedTextInSec = wordCount(highlightText) * lengthPerWord;

    const wordDuration = (text: string) => wordCount(text) * lengthPerWord;

    const getPayload = () => {
      switch (type) {
        case "FULL":
          return {
            TranscriptionId: idTranscription,
            NewBlocks: [{ StartTimeSec: transcript.endTimeSec, EndTimeSec: transcript.endTimeSec, Speaker: transcript.speaker, Text: "" }],
            TranscriptionBlocks: [{ TranscriptionBlockId: transcript.idTranscriptionBlock, StartTimeSec: transcript.startTimeSec, EndTimeSec: transcript.endTimeSec, Speaker: transcript.speaker, Text: transcript.transcript }],
          };

        case "START":
          return {
            TranscriptionId: idTranscription,
            NewBlocks: [
              {
                StartTimeSec: isLargerThanEndTime(transcript.startTimeSec + highlightedTextInSec, transcript.endTimeSec),
                EndTimeSec: transcript.endTimeSec,
                Speaker: transcript.speaker,
                Text: remainingText[0],
              },
            ],
            TranscriptionBlocks: [
              {
                TranscriptionBlockId: transcript.idTranscriptionBlock,
                StartTimeSec: isLargerThanEndTime(transcript.startTimeSec, transcript.endTimeSec),
                EndTimeSec: isLargerThanEndTime(transcript.startTimeSec + highlightedTextInSec, transcript.endTimeSec),
                Speaker: transcript.speaker,
                Text: highlightText,
              },
            ],
          };

        case "CENTER":
          const firstBlockEnd = transcript.startTimeSec + wordDuration(remainingText[0]);
          const highlightEnd = firstBlockEnd + highlightedTextInSec;

          return {
            TranscriptionId: idTranscription,
            NewBlocks: [
              {
                StartTimeSec: isLargerThanEndTime(firstBlockEnd, transcript.endTimeSec),
                EndTimeSec: isLargerThanEndTime(highlightEnd, transcript.endTimeSec),
                Speaker: transcript.speaker,
                Text: highlightText,
              },
              {
                StartTimeSec: isLargerThanEndTime(highlightEnd, transcript.endTimeSec),
                EndTimeSec: transcript.endTimeSec,
                Speaker: transcript.speaker,
                Text: remainingText[1],
              },
            ],
            TranscriptionBlocks: [
              {
                TranscriptionBlockId: transcript.idTranscriptionBlock,
                StartTimeSec: transcript.startTimeSec,
                EndTimeSec: isLargerThanEndTime(firstBlockEnd, transcript.endTimeSec),
                Speaker: transcript.speaker,
                Text: remainingText[0],
              },
            ],
          };

        case "END":
          const splitPoint = transcript.startTimeSec + wordDuration(remainingText[0]);

          return {
            TranscriptionId: idTranscription,
            NewBlocks: [
              {
                StartTimeSec: isLargerThanEndTime(splitPoint, transcript.endTimeSec),
                EndTimeSec: transcript.endTimeSec,
                Speaker: transcript.speaker,
                Text: highlightText,
              },
            ],
            TranscriptionBlocks: [
              {
                TranscriptionBlockId: transcript.idTranscriptionBlock,
                StartTimeSec: transcript.startTimeSec,
                EndTimeSec: isLargerThanEndTime(splitPoint, transcript.endTimeSec),
                Speaker: transcript.speaker,
                Text: remainingText[0],
              },
            ],
          };

        default:
          return null;
      }
    };

    const payload = getPayload();
    await dispatch(splitSegment({ id: idTranscription, data: payload }));
    dispatch(resetHighlightText());
  };

  // const handleSplitBlock = async () => ;

  //This function is being used to update time

  const handleUpdateTime = async (time: number, type: "START" | "END") => {
    let payload;
    if (type === "START") {
      setStartTime(time);
      payload = {
        TranscriptionId: idTranscription,
        TranscriptionBlocks: [
          {
            TranscriptionBlockId: props.transcript.idTranscriptionBlock,
            StartTimeSec: time,
            EndTimeSec: props.transcript.endTimeSec,
            Speaker: props.transcript.speaker,
            Text: props.transcript.transcript,
          },
        ],
      };
    } else {
      setEndTime(time);
      payload = {
        TranscriptionId: idTranscription,
        TranscriptionBlocks: [
          {
            TranscriptionBlockId: props.transcript.idTranscriptionBlock,
            StartTimeSec: props.transcript.startTimeSec,
            EndTimeSec: time,
            Speaker: props.transcript.speaker,
            Text: props.transcript.transcript,
          },
        ],
      };
    }

    await dispatch(updateTime({ id: idTranscription, data: payload }));
  };

  // useEffect(() => {
  //   const handleKeyDown = (event: KeyboardEvent) => {
  //     if (event.ctrlKey && event.shiftKey && event.key === "G") {
  //       console.log("merge block");
  //       event.preventDefault();
  //       dispatch(handleMergeBlock(currentActiveBlockIndex));
  //     } else if (event.ctrlKey && event.shiftKey && event.key === "T") {
  //       event.preventDefault();
  //       handleSplitBlockCall();
  //     } else if (event.ctrlKey && event.shiftKey && event.key === "A") {
  //       event.preventDefault();
  //       handleAddBlock();
  //     } else if (event.ctrlKey && event.shiftKey && event.key === "D") {
  //       event.preventDefault();
  //       dispatch(openQcDetailAudioBlockDeleteModal());
  //       dispatch(setSelectedTranscriptionBlock(props.transcript.idTranscriptionBlock));
  //     }
  //   };

  //   window.addEventListener("keydown", handleKeyDown);

  //   return () => {
  //     window.removeEventListener("keydown", handleKeyDown);
  //   };
  // }, []);

  return (
    <div className="d-flex justify-content-between align-items-center gap-3 transcript-action-panel position-relative">
      <div
        className="speaker-color position-absolute"
        style={{ backgroundColor: `${speakerColor()}` }}
      />
      <div className="d-flex align-items-center gap-1">
        <Select
          className="speaker-select"
          loading={updateBlockLoading}
          value={props.transcript.speaker}
          getPopupContainer={(triggerNode) => triggerNode.parentNode}
          disabled={props.disabled}
          options={speakerOption}
          onChange={(value) => {
            updateSpeaker(value);
          }}
        />
        {currentTime >= props.transcript.startTimeSec && currentTime <= props.transcript.endTimeSec && isPlaying ? (
          <TooltipButton
            text="Click to pause this segment"
            type="dark"
            iconName="Pause"
            onClick={() => {
              dispatch(setPlaying(false));
            }}
          />
        ) : (
          <TooltipButton
            text="Click to play this segment"
            loading={mergeLoading}
            type="dark"
            iconName="Play"
            onClick={() => {
              if (isPlaying) {
                dispatch(setPlaying(false));
              } else {
                dispatch(setCurrentTimeValue(props.transcript.startTimeSec));
                dispatch(setPlaying(true));
              }
            }}
          />
        )}
        <TimePicker
          className="time-picker"
          disabled={props.disabled || updateTimeLoading}
          allowClear={false}
          needConfirm={false}
          suffixIcon={updateTimeLoading ? <LoadingOutlined spin /> : undefined}
          onChange={(time, timeString) => {
            //I want to convert time to second

            const convertTime = time.hour() * 3600 + time.minute() * 60 + time.second();

            if (convertTime > props.transcript.endTimeSec) {
              return;
            }
            handleUpdateTime(convertTime, "START");
          }}
          value={secondsToCustomFormat(startTime)}
        />
        /
        <TimePicker
          className="time-picker"
          disabled={props.disabled || updateTimeLoading}
          allowClear={false}
          needConfirm={false}
          suffixIcon={updateTimeLoading ? <LoadingOutlined spin /> : undefined}
          onChange={(time, timeString) => {
            //no I want to convert the time to second so for example 1 hour is 3600 second and 1 min is 60second
            const convertTime = time.hour() * 3600 + time.minute() * 60 + time.second();
            if (convertTime < props.transcript.startTimeSec || convertTime > endTime) {
              return;
            }
            handleUpdateTime(convertTime, "END");
          }}
          value={secondsToCustomFormat(endTimeSec)}
        />
      </div>
      {!props.disabled && (
        <div className="d-flex gap-1">
          {props.transcript.showed && !props.transcript.isSelected && (
            <Checkbox
              onClick={() => {
                dispatch(handleMergeBlockSelect(props.index));
              }}
            />
          )}
          {props.transcript.isSelected ? (
            <>
              <TooltipButton
                text="Accept the merge of selected segments"
                iconName="Accept"
                type="primary"
                loading={mergeLoading}
                disabled={props.disabled || props.lastElement || selectBlockIds.length < 2}
                onClick={() => {
                  handleMergeBlockCall();
                }}
              />
              <TooltipButton
                text="Cancel the merge of selected segments"
                iconName="Cancel"
                type="secondary"
                loading={mergeLoading}
                disabled={props.disabled}
                onClick={() => {
                  dispatch(clearMergeBlock());
                }}
              />
            </>
          ) : (
            <></>
          )}

          {!(selectBlockIds.length > 0) && (
            <TooltipButton
              text="Merge segments to create a new segment [Ctrl Shift G]"
              type="primary"
              iconName="Merge"
              loading={mergeLoading}
              disabled={props.disabled || splitSegmentLoading || deleteSegmentLoading || createNewSegmentLoading}
              onClick={() => {
                dispatch(handleMergeBlock(props.index));
              }}
            />
          )}
          <TooltipButton
            text="Split this segment [Ctrl Shift T]"
            id={`split-button-${props.index}`}
            type="primary"
            iconName="Cut"
            loading={splitSegmentLoading}
            disabled={props.disabled || selectBlockIds.length > 0 || mergeLoading || deleteSegmentLoading || createNewSegmentLoading}
            onClick={() => {
              handleSplitBlockCall();
            }}
          />
          <TooltipButton
            text="Add segment new above [Ctrl Shift A]"
            type="primary"
            iconName="AddTo"
            loading={createNewSegmentLoading}
            disabled={props.disabled || selectBlockIds.length > 0 || mergeLoading || splitSegmentLoading || deleteSegmentLoading}
            onClick={() => {
              handleAddBlock();
            }}
          />
          <TooltipButton
            text="Click to delete this segment [Ctrl Shift D]"
            type="secondary"
            iconName="Delete"
            loading={deleteSegmentLoading}
            disabled={props.disabled || selectBlockIds.length > 0 || mergeLoading || splitSegmentLoading || createNewSegmentLoading}
            onClick={() => {
              dispatch(openQcDetailAudioBlockDeleteModal());
              dispatch(setSelectedTranscriptionBlock(props.transcript.idTranscriptionBlock));
            }}
          />
        </div>
      )}
    </div>
  );
}

export default TextAreaActionPanel;
